/**
 * ENLROL · El enlace «Revisar la solicitud» del correo lleva al panel de su rol
 * (OBS-UI-EL-ENLACE-DEL-CORREO-NO-LLEVA-AL-PANEL-DE-SU-ROL).
 *
 * El correo de solicitud de editor enlaza `/?admin=1` (workers/r2-handler.js)
 * y llega a administración y a moderación.
 *
 * DOP-1 (a): el panel se decide aquí, con el rol ya cargado:
 *   admin → administración · moderator → moderación · otro → sin permiso.
 *   El rol se lee de Firestore después de que Firebase avise de la sesión
 *   (AuthContext), así que se espera a un `userData` de la misma `uid`.
 * DOP-3 (a): sin sesión, se guarda la intención y se abre la entrada; al
 *   entrar, se abre el panel que toca. `recordarRevision` la vuelve a armar
 *   para «Cambiar de cuenta».
 *
 * @module hooks/useRevisionDesdeCorreo
 */

import { useCallback, useEffect, useRef } from 'react'
import type { UserRole } from '@/types/roles'

export const PARAMETRO_REVISION = 'admin'

export type PanelDeRevision = 'administracion' | 'moderacion' | 'sin-permiso'

/** Qué panel abre el enlace del correo para cada rol. */
export function panelParaRol(rol: UserRole | null | undefined): PanelDeRevision {
  if (rol === 'admin') return 'administracion'
  if (rol === 'moderator') return 'moderacion'
  return 'sin-permiso'
}

function urlPideRevision(): boolean {
  try {
    return new URLSearchParams(window.location.search).get(PARAMETRO_REVISION) === '1'
  } catch {
    return false
  }
}

export interface OpcionesRevision {
  /** uid de la sesión de Firebase, o null sin sesión. */
  uid: string | null
  /** uid y rol leídos de Firestore; null mientras no se han leído. */
  datos: { uid: string; role: UserRole } | null
  /** Firebase aún no ha dicho si hay sesión. */
  cargando: boolean
  abrirEntrada: () => void
  abrirPanel: (panel: PanelDeRevision) => void
}

export function useRevisionDesdeCorreo({
  uid, datos, cargando, abrirEntrada, abrirPanel,
}: OpcionesRevision): { recordarRevision: () => void } {
  // Marcas que no se pintan: viven en referencias, no en estado.
  const pendiente = useRef<boolean>(urlPideRevision())
  // «Cambiar de cuenta» arma la revisión con la cuenta vieja aún dentro: hasta
  // que la sesión se cierre no se decide nada, o volvería a abrirse su panel.
  const esperandoSalida = useRef(false)

  // El parámetro se quita de la dirección en cuanto se lee: la intención vive
  // en la referencia y no depende de que la URL sobreviva a la navegación.
  useEffect(() => {
    if (!urlPideRevision()) return
    try {
      const url = new URL(window.location.href)
      url.searchParams.delete(PARAMETRO_REVISION)
      window.history.replaceState(window.history.state, '', url.toString())
    } catch (error) {
      console.warn('[ENLROL] No se pudo limpiar la dirección:', error)
    }
  }, [])

  // DOP-3 (a): sin sesión, se invita a entrar.
  useEffect(() => {
    if (uid) return
    esperandoSalida.current = false
    if (pendiente.current && !cargando) abrirEntrada()
  }, [cargando, uid, abrirEntrada])

  // DOP-1 (a): con el rol de esta misma cuenta, se abre el panel que toca.
  const rolDeEstaCuenta = uid && datos && datos.uid === uid ? datos.role : null
  useEffect(() => {
    if (!pendiente.current || esperandoSalida.current || !rolDeEstaCuenta) return
    pendiente.current = false
    abrirPanel(panelParaRol(rolDeEstaCuenta))
  }, [rolDeEstaCuenta, abrirPanel])

  const recordarRevision = useCallback(() => {
    esperandoSalida.current = true
    pendiente.current = true
  }, [])
  return { recordarRevision }
}
